import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import type {
  Coords,
  GridLayer,
  Handler,
  LatLng,
  LatLngExpression,
  LeafletMouseEvent,
  Polyline,
  PolylineOptions,
} from 'leaflet';
import Leaflet from 'leaflet';
import hotline from 'leaflet-hotline';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import { initSmoothZoom } from '@karnott/map';
import 'leaflet.gridlayer.googlemutant';
import 'leaflet.vectorgrid';
import 'leaflet/dist/leaflet.css';
import './leaflet.global';

declare module 'leaflet' {
  interface HotlineOptions extends PolylineOptions {
    min?: number;
    max?: number;
    palette?: Record<number, string>;
    outlineColor?: string;
    outlineWidth?: number;
  }

  class Hotline extends Polyline {
    constructor(latlngs: LatLngExpression[] | LatLngExpression[][], options?: HotlineOptions);
    override options: HotlineOptions;
  }

  function hotline(latlngs: LatLngExpression[] | LatLngExpression[][], options?: HotlineOptions): Hotline;

  // oxlint-disable-next-line typescript/no-namespace
  namespace PM {
    export function initialize(options: { optIn: boolean }): void;
  }

  // oxlint-disable-next-line typescript/no-namespace
  namespace Draw {
    export class Polyline extends Handler {
      _onTouch(e: LeafletMouseEvent): void;
    }
  }

  interface Map {
    _zoom: number;
    _animatingZoom: boolean;
    _animateToZoom: number;
  }

  class VectorGrid extends GridLayer {
    _tileZoom: number | undefined;
    _abortController: AbortController;
    _isCurrentTile(coords: Coords): boolean;
    _setView(center: LatLng, zoom: number, noPrune?: boolean, noUpdate?: boolean): void;
  }

  // oxlint-disable-next-line typescript/no-namespace
  namespace VectorGrid {
    export class Protobuf extends VectorGrid {}
  }
}

const L = hotline(Leaflet);
L.PM.initialize({ optIn: true });
L.Draw.Polyline.prototype._onTouch = L.Util.falseFn;

initSmoothZoom(L);

/*
 * A pinch gesture leaves `map._zoom` fractional, while a tile's `coords.z` is always an integer.
 * Both comparisons below are the vectorgrid fork's own, and both go wrong on that fraction: tiles
 * created mid-pinch resolve empty without ever being fetched, and their in-flight requests are
 * aborted on every frame. Leaflet caches those empty tiles as loaded, so the layer stays blank
 * until it is rebuilt. Comparing the rounded zoom is what the fork means in both places.
 *
 * The viewport check the fork also ran in `_isCurrentTile` is dropped: a response landing while the
 * gesture is still running was discarded and never retried. Leaflet already limits tile creation to
 * the viewport plus `keepBuffer`.
 */
L.VectorGrid.Protobuf.prototype._isCurrentTile = function (coords) {
  if (!this._map) return true;
  const zoom = this._map._animatingZoom ? this._map._animateToZoom : this._map._zoom;
  return Math.round(zoom) === coords.z;
};

L.VectorGrid.Protobuf.prototype._setView = function (center, zoom, noPrune, noUpdate) {
  if (Math.round(zoom) !== this._tileZoom) {
    this._abortController.abort();
    this._abortController = new AbortController();
  }
  L.VectorGrid.prototype._setView.call(this, center, zoom, noPrune, noUpdate);
};

export { L };

export function setSmoothZoom(enable: boolean, map: Leaflet.Map) {
  if (enable) {
    map.options.zoomSnap = 0;
    map.options.zoomDelta = 0.5;
    map.scrollWheelZoom.disable();
  } else {
    map.options.zoomSnap = 1;
    map.options.zoomDelta = 1;
    map.scrollWheelZoom.enable();
  }
}

const stopPropagation = (e: Event) => e.stopPropagation();

export function setScrollZoom(enable: boolean, map: Leaflet.Map) {
  const container = map.getContainer();
  if (enable) {
    container.removeEventListener('wheel', stopPropagation, true);
  } else {
    container.addEventListener('wheel', stopPropagation, true);
  }
}
